"use client";

import React, { useEffect, useRef, useState } from "react";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Droplets, Bug, ShieldAlert, Repeat, Octagon } from "lucide-react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.1,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const insightsData = [{
  title: "Extreme Humidity",
  description: "Florida's moisture-rich environment accelerates pest breeding cycles, turning minor issues into severe infestations rapidly.",
  icon: Droplets
}, {
  title: "Breeding Sources",
  description: "From standing water to dense tropical vegetation, our local landscape provides endless safe havens for mosquitoes and roaches.",
  icon: Bug
}, {
  title: "The DIY Trap",
  description: "Over-the-counter sprays only treat the surface. They fail to eliminate the root cause or penetrate deep nesting areas.",
  icon: ShieldAlert
}, {
  title: "Relentless Pressure",
  description: "Pest activity doesn't stop for winter here. Year-round, proactive protection is the only way to maintain a pest-free home.",
  icon: Repeat
}];
export default function EducationalPestInsights() {
  const headerReveal = useRevealOnScroll<HTMLDivElement>();
  const gridReveal = useRevealOnScroll<HTMLDivElement>();
  const ctaReveal = useRevealOnScroll<HTMLDivElement>();
  return <section id="educationalpestinsights" className="bg-background text-foreground py-24 lg:py-32 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        {}
        <div ref={headerReveal.ref} className={`max-w-5xl transition-all duration-1000 ease-out ${headerReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <h2 className="font-default text-5xl md:text-7xl lg:text-8xl font-bold uppercase leading-[0.9] tracking-tighter text-foreground">
            Why Florida's Climate Demands Professional Pest Control
          </h2>
        </div>

        {}
        <div ref={gridReveal.ref} className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-16 lg:mt-24 transition-all duration-1000 delay-200 ease-out ${gridReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {insightsData.map((insight, i) => {
          const Icon = insight.icon;
          return <Card key={i} data-index={i} className="rounded-none border-4 border-foreground bg-card shadow-none hover:-translate-y-2 transition-transform duration-300">
                <CardHeader className="pb-4">
                  <div className="relative w-16 h-16 mb-4 text-foreground">
                    <Octagon className="absolute inset-0 w-full h-full stroke-[1.5]" />
                    <Icon className="absolute inset-0 w-1/2 h-1/2 m-auto stroke-[2]" />
                  </div>
                  <CardTitle className="font-default text-2xl uppercase tracking-tight">
                    {insight.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="font-serif text-lg leading-relaxed text-card-foreground/90">
                    {insight.description}
                  </p>
                </CardContent>
              </Card>;
        })}
        </div>

        {}
        <div ref={ctaReveal.ref} className={`mt-24 lg:mt-32 border-t-8 border-foreground pt-12 lg:pt-16 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-10 transition-all duration-1000 delay-400 ease-out ${ctaReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <h3 className="font-default text-4xl md:text-5xl lg:text-6xl max-w-3xl uppercase leading-none tracking-tighter">
            Don't Wait for an Infestation to Escalate.
          </h3>
          <Button asChild size="lg" className="rounded-full text-lg md:text-xl px-10 py-8 uppercase tracking-wider font-default shrink-0 hover:scale-105 transition-transform duration-300">
            <Link to="/ContactPage">Schedule Free Inspection</Link>
          </Button>
        </div>
      </div>
    </section>;
}